import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, ShieldCheckIcon, CheckCircle2Icon, SparklesIcon, LockIcon } from 'lucide-react';
interface ExitIntentPopupProps {
  isOpen: boolean;
  onAccept: () => void;
  onDecline: () => void;
  onClose: () => void;
}
const DOLLAR = '\u0024';
const FULL_PRICE = DOLLAR + '33.74';
const BUNDLE_PRICE = DOLLAR + '27.82';
const SAVINGS = DOLLAR + '5.92';
export function ExitIntentPopup({
  isOpen,
  onAccept,
  onDecline,
  onClose
}: ExitIntentPopupProps) {
  return <AnimatePresence data-id="element-133">
      {isOpen && <>
          {/* Overlay */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="fixed inset-0 bg-black/60 z-[60]" onClick={onClose} data-id="element-134" />

          {/* Popup */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.9,
        y: 40
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.35,
        ease: 'easeOut'
      }} className="fixed inset-0 z-[61] flex items-center justify-center p-4 pointer-events-none" data-id="element-135">
            <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto pointer-events-auto relative" onClick={e => e.stopPropagation()} data-id="element-136">
              {/* Close Button */}
              <button onClick={onClose} className="absolute top-3 right-3 text-slate hover:text-navy transition-colors z-10 p-1" aria-label="Close popup" data-id="element-137">
                <XIcon className="w-5 h-5" data-id="element-138" />
              </button>

              {/* Header Banner */}
              <div className="bg-navy text-white px-6 py-5 rounded-t-2xl text-center relative overflow-hidden" data-id="element-139">
                <div className="absolute inset-0 bg-gradient-to-r from-navy-dark/50 via-transparent to-navy-dark/50" data-id="element-140" />
                <div className="relative z-10" data-id="element-141">
                  <span className="inline-block bg-amber-500 text-white text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full mb-3 animate-pulse" data-id="element-142">
                    Limited Time — This Screen Only
                  </span>
                  <h2 className="text-2xl md:text-3xl font-heading font-bold leading-tight mb-2" data-id="element-143">
                    ⚠️ Wait — You're Leaving Money on the Table
                  </h2>
                  <p className="text-white/80 font-body text-sm md:text-base" data-id="element-144">
                    What if you could protect yourself AND your family for less?
                  </p>
                </div>
              </div>

              {/* Body */}
              <div className="px-6 py-6 space-y-5" data-id="element-145">
                {/* Discount Badge */}
                <div className="flex justify-center" data-id="element-146">
                  <span className="inline-flex items-center gap-2 bg-red-50 text-red-700 font-bold text-sm px-4 py-2 rounded-full border border-red-200 exit-intent-flash" data-id="element-147">
                    <SparklesIcon className="w-4 h-4" data-id="element-148" />
                    30% OFF All Add-Ons — Save {SAVINGS}
                  </span>
                </div>

                {/* Main Copy */}
                <div className="text-center space-y-3" data-id="element-149">
                  <p className="text-text-dark font-body leading-relaxed" data-id="element-150">
                    The Survival Kit gives you the plan — but the add-ons give
                    you the tools to{' '}
                    <strong className="text-navy" data-id="element-151">
                      organize the paperwork
                    </strong>{' '}
                    and{' '}
                    <strong className="text-navy" data-id="element-152">
                      lead the family conversation
                    </strong>{' '}
                    without the breakdown.
                  </p>
                  <p className="text-text-muted font-body text-sm" data-id="element-153">
                    Get the complete package: the 35-page guide, all 4 bonuses,
                    plus both tactical add-ons — at a price lower than anywhere
                    else.
                  </p>
                </div>

                {/* What's Included */}
                <div className="bg-warm-white rounded-xl p-4 space-y-2.5" data-id="element-154">
                  <h4 className="font-heading font-bold text-navy text-sm uppercase tracking-wide mb-3" data-id="element-155">
                    Everything Included:
                  </h4>
                  <div className="space-y-2" data-id="element-156">
                    {['The Inherited House Survival Kit (35-page PDF)', 'BONUS: The Letter of Intent Template', 'BONUS: The Bank Call Script Pack (7 scripts)', 'BONUS: The 72-Hour House Lockdown Checklist', 'BONUS: The Sell vs. Rent vs. Hold Calculator', "ADD-ON: The Executor's Shoebox Sort — organize 20+ years in 1 afternoon", 'ADD-ON: The Family Meeting Survival Script — stop arguments before they start'].map((item, i) => <div key={i} className="flex items-start gap-2.5" data-id="element-157">
                        <CheckCircle2Icon className="w-4 h-4 text-green-600 shrink-0 mt-0.5" data-id="element-158" />
                        <span className="text-text-dark font-body text-sm leading-snug" data-id="element-159">
                          {item}
                        </span>
                      </div>)}
                  </div>
                </div>

                {/* Pricing Block */}
                <div className="bg-navy/5 rounded-xl p-5 text-center space-y-2 border border-navy/10" data-id="element-160">
                  <div className="flex items-center justify-center gap-3" data-id="element-161">
                    <span className="text-slate line-through text-lg font-body" data-id="element-162">
                      {FULL_PRICE}
                    </span>
                    <span className="text-3xl font-heading font-bold text-navy" data-id="element-163">
                      {BUNDLE_PRICE}
                    </span>
                  </div>
                  <div className="flex items-center justify-center gap-1.5 text-text-muted text-xs font-body" data-id="element-164">
                    <LockIcon className="w-3 h-3" data-id="element-165" />
                    <span data-id="element-166">Special price only available on this screen</span>
                  </div>
                </div>

                {/* CTA Buttons */}
                <div className="space-y-3" data-id="element-167">
                  <button onClick={onAccept} className="w-full bg-accent hover:bg-accent-hover text-white font-body font-bold text-base md:text-lg py-4 px-6 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg exit-intent-cta-pulse" data-id="element-168">
                    YES! I Want the Complete Package for {BUNDLE_PRICE}
                  </button>
                  <button onClick={onDecline} className="w-full text-text-muted hover:text-navy font-body text-sm py-2 transition-colors underline underline-offset-2" data-id="element-169">
                    No thanks, continue without the extras →
                  </button>
                </div>

                {/* Trust Badge */}
                <div className="flex items-center justify-center gap-2 text-text-muted text-xs font-body pt-1" data-id="element-170">
                  <ShieldCheckIcon className="w-4 h-4 text-green-600" data-id="element-171" />
                  <span data-id="element-172">
                    30-Day Money-Back Guarantee · Instant Download · Secure
                    Checkout
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}